/**
 * ContextToolbar - Floating toolbar that appears next to selected elements
 * 
 * Context-aware buttons based on element type:
 * - Text: Edit Text, Design, Animate
 * - Image: Change Image, Design, Animate
 * - Button: Edit Text, Link, Design, Animate
 * - Card: Edit Content, Design, Animate
 * 
 * Uses @floating-ui/react for positioning
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  useFloating, 
  offset, 
  flip, 
  shift, 
  autoUpdate,
  FloatingPortal
} from '@floating-ui/react';
import { 
  Type, Image, MousePointer2, Palette, Sparkles, 
  Link2, Pencil, RotateCcw, X
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider
} from '../../ui/tooltip';
import FloatingPanel from './FloatingPanel';

// Toolbar button configs by element type
const TOOLBAR_CONFIGS = {
  text: [
    { id: 'edit', icon: Pencil, label: 'Edit Text', panel: 'text' },
    { id: 'design', icon: Palette, label: 'Design', panel: 'design' },
    { id: 'animate', icon: Sparkles, label: 'Animate', panel: 'animate' }
  ],
  heading: [
    { id: 'edit', icon: Pencil, label: 'Edit Text', panel: 'text' },
    { id: 'design', icon: Palette, label: 'Design', panel: 'design' },
    { id: 'animate', icon: Sparkles, label: 'Animate', panel: 'animate' }
  ],
  badge: [
    { id: 'edit', icon: Pencil, label: 'Edit Text', panel: 'text' },
    { id: 'design', icon: Palette, label: 'Design', panel: 'design' },
    { id: 'animate', icon: Sparkles, label: 'Animate', panel: 'animate' }
  ],
  image: [
    { id: 'change', icon: Image, label: 'Change Image', panel: 'image' },
    { id: 'design', icon: Palette, label: 'Design', panel: 'design' },
    { id: 'animate', icon: Sparkles, label: 'Animate', panel: 'animate' }
  ],
  button: [
    { id: 'edit', icon: Pencil, label: 'Edit Text', panel: 'text' },
    { id: 'link', icon: Link2, label: 'Link', panel: 'link' },
    { id: 'design', icon: Palette, label: 'Design', panel: 'design' },
    { id: 'animate', icon: Sparkles, label: 'Animate', panel: 'animate' }
  ],
  card: [
    { id: 'edit', icon: Pencil, label: 'Edit Content', panel: 'content' },
    { id: 'design', icon: Palette, label: 'Design', panel: 'design' },
    { id: 'animate', icon: Sparkles, label: 'Animate', panel: 'animate' }
  ],
  default: [
    { id: 'design', icon: Palette, label: 'Design', panel: 'design' },
    { id: 'animate', icon: Sparkles, label: 'Animate', panel: 'animate' }
  ]
};

const ContextToolbar = ({
  elementRect,
  elementType = 'text',
  elementId,
  elementLabel,
  blockProps,
  tokens,
  onUpdateValue,
  onUpdateStyles,
  onUpdateMotion,
  onReplayAnimation,
  onClose,
  containerRef
}) => {
  const [activePanel, setActivePanel] = useState(null);
  const toolbarRef = useRef(null);
  const floatingRef = useRef(null);
  
  // Get toolbar config based on element type
  const toolbarConfig = TOOLBAR_CONFIGS[elementType] || TOOLBAR_CONFIGS.default;
  
  // Floating UI for toolbar positioning
  const { refs, floatingStyles, update } = useFloating({
    placement: 'top',
    middleware: [
      offset(8),
      flip({ fallbackPlacements: ['bottom', 'right', 'left'] }),
      shift({ padding: 8 })
    ],
    whileElementsMounted: autoUpdate
  });

  // Set floating ref callback
  const setFloatingRef = useCallback((node) => {
    floatingRef.current = node;
    refs.setFloating(node);
  }, [refs]);

  // Update position when rect changes
  useEffect(() => {
    if (elementRect) {
      // Create a virtual element from the rect
      refs.setReference({
        getBoundingClientRect: () => elementRect
      });
    }
  }, [elementRect, refs]);

  // Update on scroll/resize
  useEffect(() => {
    if (!containerRef?.current) return;
    
    const handleUpdate = () => {
      if (update) update();
    };
    
    const container = containerRef.current;
    container.addEventListener('scroll', handleUpdate, { passive: true });
    window.addEventListener('resize', handleUpdate);
    
    return () => {
      container.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [containerRef, update]);

  const handleButtonClick = (panelType) => {
    setActivePanel(activePanel === panelType ? null : panelType);
  };

  const handleClosePanel = () => {
    setActivePanel(null);
  };

  if (!elementRect) return null;

  return (
    <TooltipProvider>
      <FloatingPortal>
        {/* Toolbar */}
        <div
          ref={setFloatingRef}
          style={{
            ...floatingStyles,
            zIndex: 9999
          }}
          className="pointer-events-auto"
        >
          <div 
            ref={toolbarRef}
            className="flex items-center gap-0.5 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl p-1"
            data-testid="context-toolbar"
          >
            {/* Element type indicator */}
            <span className="text-[10px] text-zinc-500 px-2 border-r border-zinc-700 mr-1 capitalize">
              {elementType}
            </span>
            
            {/* Action buttons */}
            {toolbarConfig.map((btn) => (
              <Tooltip key={btn.id}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleButtonClick(btn.panel)}
                    className={`p-2 rounded-md transition-colors ${
                      activePanel === btn.panel
                        ? 'bg-orange-500 text-white'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                    }`}
                    data-testid={`toolbar-${btn.id}`}
                  >
                    <btn.icon className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">{btn.label}</p>
                </TooltipContent>
              </Tooltip>
            ))}
            
            {/* Close button */}
            <div className="border-l border-zinc-700 ml-1 pl-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-md text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">Close</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
          
          {/* Floating Panel */}
          {activePanel && (
            <FloatingPanel
              panelType={activePanel}
              elementId={elementId}
              elementType={elementType}
              elementLabel={elementLabel}
              blockProps={blockProps}
              tokens={tokens}
              onUpdateValue={onUpdateValue}
              onUpdateStyles={onUpdateStyles}
              onUpdateMotion={onUpdateMotion}
              onReplayAnimation={onReplayAnimation}
              onClose={handleClosePanel}
              anchorRef={toolbarRef}
            />
          )}
        </div>
      </FloatingPortal>
    </TooltipProvider>
  );
};

export default ContextToolbar;
